'use client';

import type {
  ILineAction,
  ILineFlexBubble,
  ILineFlexComponent,
} from '@/interfaces/lineDemo';

interface IPreviewContent {
  imageUrl?: string;
  texts: string[];
  actions: ILineAction[];
}

function collectComponent(
  component: ILineFlexComponent | undefined,
  result: IPreviewContent
): void {
  if (!component) return;
  if (component.type === 'image' && component.url && !result.imageUrl) {
    result.imageUrl = component.url;
  }
  if (component.type === 'text' && component.text)
    result.texts.push(component.text);
  if (component.action) result.actions.push(component.action);
  component.contents?.forEach(child => collectComponent(child, result));
}

function bubbleContent(bubble: ILineFlexBubble): IPreviewContent {
  const result: IPreviewContent = { texts: [], actions: [] };
  collectComponent(bubble.header, result);
  collectComponent(bubble.hero, result);
  collectComponent(bubble.body, result);
  collectComponent(bubble.footer, result);
  return result;
}

function Bubble({
  bubble,
  onAction,
  accent,
}: {
  bubble: ILineFlexBubble;
  onAction: (action: ILineAction) => void;
  accent: string;
}) {
  const content = bubbleContent(bubble);
  return (
    <div className="w-[270px] shrink-0 overflow-hidden rounded-2xl bg-white shadow-sm">
      {content.imageUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={content.imageUrl}
          alt=""
          className="h-32 w-full object-cover"
        />
      )}
      <div className="space-y-1.5 px-4 py-3">
        {content.texts.map((value, index) => (
          <div
            key={`${value}-${index}`}
            className={
              index === 0
                ? 'text-sm font-bold leading-5 text-[#24272b]'
                : 'text-xs leading-5 text-[#697078]'
            }
          >
            {value}
          </div>
        ))}
      </div>
      {content.actions.length > 0 && (
        <div className="divide-y divide-black/5 border-t border-black/5">
          {content.actions.slice(0, 4).map((action, index) => (
            <button
              key={`${action.label || action.text || action.uri}-${index}`}
              type="button"
              onClick={() => onAction(action)}
              className="block w-full px-4 py-3 text-center text-xs font-bold hover:bg-black/[0.03]"
              style={{ color: accent }}
            >
              {action.label || action.text || 'Open'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LineFlexPreview({
  contents,
  onAction,
  accent = '#079c47',
}: {
  contents: ILineFlexBubble | { type: 'carousel'; contents: ILineFlexBubble[] };
  onAction: (action: ILineAction) => void;
  /** Button colour for the channel skin. */
  accent?: string;
}) {
  const bubbles = contents.type === 'carousel' ? contents.contents : [contents];
  return (
    <div className="flex max-w-full gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {bubbles.map((bubble, index) => (
        <Bubble
          key={index}
          bubble={bubble}
          onAction={onAction}
          accent={accent}
        />
      ))}
    </div>
  );
}
